import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { fetchDecisionsRequest, fetchDecisionDetailRequest, clearDecisionDetail, workflowActionRequest } from '../store/decisions/actions';
import { fetchDecisionCommentaires } from '../store/decisions/service';
import { fetchUsersRequest } from '../store/users/actions';
import { toast } from 'react-toastify';
import {
  Search,
  Eye,
  X,
  MessageSquare,
  CheckCircle,
  XCircle,
  Send,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import './AdminDecisions.css';

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#D97706', bg: '#FFF7ED' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  anonymise:  { label: 'Anonymisé',    color: '#2563EB', bg: '#EFF6FF' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
  rejete:     { label: 'Rejeté',       color: '#DC2626', bg: '#FEF2F2' },
};

const signalTypeLabels: Record<string, { label: string; color: string; bg: string }> = {
  erreur_anonymisation: { label: 'Anonymisation', color: '#059669', bg: '#ECFDF5' },
  erreur_ocr:           { label: 'OCR',           color: '#E85D04', bg: '#FFF3E8' },
  contenu_manquant:     { label: 'Contenu manquant', color: '#DC2626', bg: '#FEF2F2' },
  metadonnee_incorrecte:{ label: 'Métadonnée',    color: '#6B7280', bg: '#F3F4F6' },
  autre:                { label: 'Autre',          color: '#7C3AED', bg: '#F5F3FF' },
};

const PAGE_SIZE = 20;

const AdminDecisions: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { decisions, isLoading, count, next, previous, detail, detailLoading } = useSelector(
    (state: RootState) => state.decisions
  );
  const { users } = useSelector((state: RootState) => state.users);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatut, setSelectedStatut] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [expandedSignal, setExpandedSignal] = useState<number | null>(null);
  const [signalements, setSignalements] = useState<any[]>([]);
  const [signalementsLoading, setSignalementsLoading] = useState(false);

  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Comments modal state
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsDecisionId, setCommentsDecisionId] = useState<string | null>(null);
  const [commentsDecisionNumero, setCommentsDecisionNumero] = useState<string>('');
  const [commentsData, setCommentsData] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const fetchPage = useCallback((page: number, search?: string, statut?: string, agent?: string) => {
    const params: any = { page, page_size: PAGE_SIZE };
    if (search) params.search = search;
    if (statut) params.statut = statut;
    if (agent) params.agent = agent;
    dispatch(fetchDecisionsRequest(params));
  }, [dispatch]);

  useEffect(() => {
    fetchPage(1);
    dispatch(fetchUsersRequest({ limit: 200 }));
  }, [fetchPage, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  const handleOpenDecision = (id: string) => {
    setOpenDropdownId(null);
    navigate(`/espace-admin/decisions/${id}`);
  };

  const handleOpenComments = async (id: string, numero: string) => {
    setOpenDropdownId(null);
    setCommentsDecisionId(id);
    setCommentsDecisionNumero(numero || '—');
    setCommentsModalOpen(true);
    setCommentsLoading(true);
    setCommentsData([]);
    try {
      const response = await fetchDecisionCommentaires(id);
      const data = response.data;
      setCommentsData(Array.isArray(data) ? data : data.results || []);
    } catch {
      setCommentsData([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const closeCommentsModal = () => {
    setCommentsModalOpen(false);
    setCommentsDecisionId(null);
    setCommentsData([]);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      fetchPage(1, value, selectedStatut, selectedAgent);
    }, 400);
    setSearchTimeout(timeout);
  };

  const handleStatutChange = (value: string) => {
    setSelectedStatut(value);
    setCurrentPage(1);
    fetchPage(1, searchQuery, value, selectedAgent);
  };

  const handleAgentChange = (value: string) => {
    setSelectedAgent(value);
    setCurrentPage(1);
    fetchPage(1, searchQuery, selectedStatut, value);
  };

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (isLoading) return;
    const url = direction === 'next' ? next : previous;
    if (!url) return;
    const parsed = new URL(url);
    const params: any = {};
    parsed.searchParams.forEach((value, key) => { params[key] = value; });
    dispatch(fetchDecisionsRequest(params));
    setCurrentPage(prev => direction === 'next' ? Math.min(prev + 1, totalPages) : Math.max(prev - 1, 1));
  };

  const handleViewDetail = async (id: string) => {
    dispatch(fetchDecisionDetailRequest(id));
    setPanelOpen(true);
    setSignalements([]);
    setSignalementsLoading(true);
    try {
      const response = await fetchDecisionCommentaires(id);
      const data = response.data;
      setSignalements(Array.isArray(data) ? data : data.results || []);
    } catch {
      setSignalements([]);
    } finally {
      setSignalementsLoading(false);
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    dispatch(clearDecisionDetail());
    setExpandedSignal(null);
    setSignalements([]);
  };

  const getAgentName = (agent: any) => {
    if (!agent) return '—';
    if (agent.first_name || agent.last_name) {
      return `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
    }
    return agent.email || '—';
  };

  const handleWorkflowAction = (action: 'valider' | 'rejeter' | 'publier' | 'remettre_en_verification') => {
    if (!detail) return;
    dispatch(workflowActionRequest(detail.id, action, () => {
      toast.success(`Action "${action}" effectuée.`);
      dispatch(fetchDecisionDetailRequest(detail.id));
      fetchPage(currentPage, searchQuery, selectedStatut, selectedAgent);
    }));
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="admin-dec-page">
      {/* Filters */}
      <div className="admin-dec-filters">
        <div className="admin-dec-search">
          <Search size={16} className="admin-dec-search-icon" />
          <input
            type="text"
            placeholder="Rechercher un numéro, des parties..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <select
          value={selectedStatut}
          onChange={(e) => handleStatutChange(e.target.value)}
          className="admin-dec-select"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(statutLabels).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <select
          value={selectedAgent}
          onChange={(e) => handleAgentChange(e.target.value)}
          className="admin-dec-select"
        >
          <option value="">Tous les agents</option>
          {users.filter(u => u.is_active).map(u => (
            <option key={u.id} value={u.id}>
              {u.first_name || u.last_name
                ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                : u.email}
            </option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="admin-dec-results-header">
        <span className="admin-dec-count">
          {isLoading ? 'Chargement...' : `${count} résultat${count > 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Table */}
      <div className="admin-dec-table-wrap">
        {isLoading && decisions.length === 0 ? (
          <div className="admin-dec-loading-full">
            <Loader size={28} className="admin-dec-spinner" />
            <span>Chargement des décisions...</span>
          </div>
        ) : (
          <>
            <table className="admin-dec-table">
              <thead>
                <tr>
                  <th>Date traitement</th>
                  <th>N° Décision</th>
                  <th>Juridiction</th>
                  <th>Date décision</th>
                  <th>Statut</th>
                  <th>Agent</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {decisions.filter(d => d.date_decision).map(d => {
                  const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                  return (
                    <tr key={d.id} className={panelOpen && detail?.id === d.id ? 'row-active' : ''}>
                      <td className="td-date-traitement">
                        {d.created_at
                          ? (() => {
                              const dt = new Date(d.created_at);
                              const date = dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                              const time = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                              return `${date} à ${time}`;
                            })()
                          : '—'}
                      </td>
                      <td className="td-numero-admin">{d.numero || '—'}</td>
                      <td>{d.juridiction || '—'}</td>
                      <td>{d.date_decision ? new Date(d.date_decision).toLocaleDateString('fr-FR') : '—'}</td>
                      <td>
                        <span className="admin-dec-badge" style={{ color: s.color, background: s.bg }}>
                          {s.label}
                        </span>
                      </td>
                      <td>{getAgentName(d.agent)}</td>
                      <td>
                        <div className="admin-dec-action-wrapper" ref={openDropdownId === d.id ? dropdownRef : null}>
                          <button
                            className="admin-dec-view-btn"
                            onClick={() => setOpenDropdownId(openDropdownId === d.id ? null : d.id)}
                            title="Actions"
                          >
                            <Eye size={16} />
                          </button>
                          {openDropdownId === d.id && (
                            <div className="admin-dec-dropdown">
                              <button
                                className="admin-dec-dropdown-item"
                                onClick={() => handleOpenDecision(d.id)}
                              >
                                <ExternalLink size={14} />
                                Ouvrir la décision
                              </button>
                              <button
                                className="admin-dec-dropdown-item"
                                onClick={() => handleOpenComments(d.id, d.numero)}
                              >
                                <MessageSquare size={14} />
                                Voir les commentaires
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {decisions.length === 0 && !isLoading && (
              <div className="admin-dec-empty">
                Aucune décision ne correspond aux critères de recherche.
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-dec-pagination">
          <button
            className="admin-dec-page-btn"
            disabled={!previous || isLoading}
            onClick={() => handlePageChange('prev')}
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <span className="admin-dec-page-info">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            className="admin-dec-page-btn"
            disabled={!next || isLoading}
            onClick={() => handlePageChange('next')}
          >
            Suivant <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Comments modal */}
      {commentsModalOpen && (
        <>
          <div className="admin-dec-modal-overlay" onClick={closeCommentsModal} />
          <div className="admin-dec-modal">
            <div className="admin-dec-modal-header">
              <h3>
                <MessageSquare size={18} /> Commentaires — {commentsDecisionNumero}
              </h3>
              <button className="admin-dec-modal-close" onClick={closeCommentsModal}>
                <X size={18} />
              </button>
            </div>
            <div className="admin-dec-modal-body">
              {commentsLoading ? (
                <div className="admin-dec-modal-loading">
                  <Loader size={22} className="admin-dec-spinner" />
                  <span>Chargement des commentaires...</span>
                </div>
              ) : commentsData.length > 0 ? (
                <div className="admin-dec-comments-list">
                  {commentsData.map((c: any, idx: number) => {
                    const st = signalTypeLabels[c.type_probleme] || { label: c.type_probleme || '—', color: '#666', bg: '#f3f4f6' };
                    const sigStatut = c.statut || 'nouveau';
                    return (
                      <div key={c.id || idx} className="admin-dec-comment-item">
                        <div className="admin-dec-comment-top">
                          <div className="admin-dec-comment-badges">
                            <span className="admin-dec-signal-type" style={{ color: st.color, background: st.bg }}>
                              {st.label}
                            </span>
                            <span className={`admin-dec-signal-status signal-status-${sigStatut}`}>
                              {sigStatut === 'traite' ? 'Traité' : 'Nouveau'}
                            </span>
                          </div>
                          <span className="admin-dec-comment-date">
                            {c.created_at
                              ? new Date(c.created_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </span>
                        </div>
                        <p className="admin-dec-comment-text">{c.commentaire || '—'}</p>
                        <span className="admin-dec-comment-author">
                          {c.agent_name || c.utilisateur || 'Utilisateur inconnu'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-dec-modal-empty">
                  Aucun commentaire pour cette décision.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Side panel */}
      {panelOpen && (
        <>
          <div className="admin-dec-overlay" onClick={closePanel} />
          <div className="admin-dec-panel">
            <button className="admin-dec-panel-close" onClick={closePanel}>
              <X size={20} />
            </button>

            {detailLoading ? (
              <div className="admin-dec-panel-loading">Chargement...</div>
            ) : detail ? (
              <div className="admin-dec-panel-content">
                <h2 className="admin-dec-panel-title">{detail.numero || '—'}</h2>
                <p className="admin-dec-panel-sub">
                  {detail.juridiction || '—'} · {detail.date_decision
                    ? new Date(detail.date_decision).toLocaleDateString('fr-FR')
                    : '—'}
                </p>

                {/* Signalements */}
                <div className="admin-dec-panel-section">
                  <div className="admin-dec-signals-header">
                    <label>
                      <MessageSquare size={14} /> SIGNALEMENTS UTILISATEURS
                    </label>
                    {signalements.length > 0 && (
                      <span className="admin-dec-signals-count">{signalements.length}</span>
                    )}
                  </div>

                  {signalementsLoading ? (
                    <p className="admin-dec-no-signals">Chargement...</p>
                  ) : signalements.length > 0 ? (
                    <div className="admin-dec-signals-list">
                      {signalements.map((sig: any, idx: number) => {
                        const st = signalTypeLabels[sig.type_probleme] || { label: sig.type_probleme || '—', color: '#666', bg: '#f3f4f6' };
                        const isExpanded = expandedSignal === idx;
                        const sigStatut = sig.statut || 'nouveau';

                        return (
                          <div key={sig.id || idx} className="admin-dec-signal-item">
                            <div
                              className="admin-dec-signal-top"
                              onClick={() => setExpandedSignal(isExpanded ? null : idx)}
                            >
                              <div className="admin-dec-signal-badges">
                                <span className="admin-dec-signal-type" style={{ color: st.color, background: st.bg }}>
                                  {st.label}
                                </span>
                                <span className={`admin-dec-signal-status signal-status-${sigStatut}`}>
                                  {sigStatut === 'traite' ? 'Traité' : 'Nouveau'}
                                </span>
                              </div>
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>

                            {isExpanded && (
                              <div className="admin-dec-signal-body">
                                <p className="admin-dec-signal-text">{sig.commentaire || '—'}</p>
                                <div className="admin-dec-signal-meta">
                                  <span>{sig.agent_name || sig.utilisateur || '—'}</span>
                                  <span>
                                    {sig.created_at
                                      ? new Date(sig.created_at).toLocaleDateString('fr-FR')
                                      : '—'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="admin-dec-no-signals">Aucun signalement.</p>
                  )}
                </div>

                {/* Actions */}
                <div className="admin-dec-panel-actions">
                  {(detail.statut === 'a_verifier' || detail.statut === 'valide' || detail.statut === 'anonymise') && (
                    <div className="admin-dec-panel-actions-row">
                      <button
                        className="admin-dec-btn-validate"
                        onClick={() => handleWorkflowAction('valider')}
                      >
                        <CheckCircle size={16} /> Valider
                      </button>
                      <button
                        className="admin-dec-btn-reject"
                        onClick={() => handleWorkflowAction('rejeter')}
                      >
                        <XCircle size={16} /> Rejeter
                      </button>
                    </div>
                  )}
                  {(detail.statut === 'valide' || detail.statut === 'anonymise') && (
                    <button
                      className="admin-dec-btn-publish"
                      onClick={() => handleWorkflowAction('publier')}
                    >
                      <Send size={16} /> Publier
                    </button>
                  )}
                  {detail.statut === 'rejete' && (
                    <button
                      className="admin-dec-btn-publish"
                      onClick={() => handleWorkflowAction('remettre_en_verification')}
                    >
                      <RotateCcw size={16} /> Restaurer
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDecisions;
